// AICA Layout Context
// Manages layout preferences (sidebar, density, etc.)
// Bloomberg Terminal style: Dense, efficient, customizable

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type SidebarPosition = 'left' | 'right';

export type Density = 'comfortable' | 'compact' | 'spacious';

export interface LayoutContextValue {
  /**
   * Sidebar position
   */
  sidebarPosition: SidebarPosition;
  /**
   * Set sidebar position
   */
  setSidebarPosition: (position: SidebarPosition) => void;
  /**
   * Interface density
   */
  density: Density;
  /**
   * Set interface density
   */
  setDensity: (density: Density) => void;
  /**
   * Sidebar collapsed state
   */
  sidebarCollapsed: boolean;
  /**
   * Toggle sidebar collapsed
   */
  toggleSidebarCollapsed: () => void;
}

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

export interface LayoutProviderProps {
  children: ReactNode;
  /**
   * Storage key for localStorage (default: 'aica-layout')
   */
  storageKey?: string;
}

/**
 * Layout Provider
 * Manages layout preferences with localStorage persistence
 */
export function LayoutProvider({
  children,
  storageKey = 'aica-layout',
}: LayoutProviderProps) {
  const [sidebarPosition, setSidebarPositionState] = useState<SidebarPosition>(() => {
    const stored = localStorage.getItem(`${storageKey}-sidebar-position`);
    if (stored === 'left' || stored === 'right') {
      return stored;
    }
    return 'left';
  });

  const [density, setDensityState] = useState<Density>(() => {
    const stored = localStorage.getItem(`${storageKey}-density`);
    if (stored === 'comfortable' || stored === 'compact' || stored === 'spacious') {
      return stored;
    }
    return 'comfortable';
  });

  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    const stored = localStorage.getItem(`${storageKey}-sidebar-collapsed`);
    return stored === 'true';
  });

  // Apply density to document
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-density', density);
  }, [density]);

  // Apply sidebar position to document
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-sidebar-position', sidebarPosition);
    // Also set shorter name for CSS selectors
    root.setAttribute('data-sidebar', sidebarPosition);
  }, [sidebarPosition]);

  const setSidebarPosition = (position: SidebarPosition) => {
    setSidebarPositionState(position);
    localStorage.setItem(`${storageKey}-sidebar-position`, position);
  };

  const setDensity = (newDensity: Density) => {
    setDensityState(newDensity);
    localStorage.setItem(`${storageKey}-density`, newDensity);
  };

  const toggleSidebarCollapsed = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsedState(newState);
    localStorage.setItem(`${storageKey}-sidebar-collapsed`, String(newState));
  };

  const value: LayoutContextValue = {
    sidebarPosition,
    setSidebarPosition,
    density,
    setDensity,
    sidebarCollapsed,
    toggleSidebarCollapsed,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

/**
 * useLayout Hook
 * Access layout context
 *
 * @example
 * ```tsx
 * const { density, setDensity } = useLayout();
 *
 * <Button onClick={() => setDensity('compact')}>
 *   Compact View
 * </Button>
 * ```
 */
export function useLayout(): LayoutContextValue {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within LayoutProvider');
  }
  return context;
}
